import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore } from '@/state/store/auth';
import { useGuestSessionStore } from '@/state/store/guestSession';
import {
  createContractDocument,
  updateContractDocument,
  getContractDocuments,
} from '@/modules/org/contracts/services/contract-document.service';
import { createContractDocumentEvent } from '@/modules/org/contracts/services/contract-document-event.service';
import type {
  OrgContractDocumentInsertInput,
  OrgContractDocumentUpdateInput,
  OrgContractDocumentItem,
  ContractSignatory,
} from '@/modules/org/contracts/types/contract-document.types';
import {
  GUEST_ORG_SENTINEL,
  serializeSignatories,
  parseSignatories,
} from '@/modules/org/contracts/types/contract-document.types';
import type { SignatoryEntry } from '@/modules/templates/lib/signatory-config';
import type { TemplateConfig } from '../DynamicFormContext';
import {
  LEGAL_TEMPLATE_DOCUMENT_GENERATION_FEATURE_ID,
  updateSubscriptionUsage,
} from '@/modules/subscription/services/subscription.service';
import { useQueryClient } from '@tanstack/react-query';
import { activeSubscriptionQueryKey } from '@/modules/subscription/hooks/use-active-subscription';

const DEBOUNCE_MS = 6_000;

interface AutoSaveDraftOptions {
  formData: Record<string, unknown>;
  enrichmentContext: Record<string, unknown>;
  dynamicFieldsCache: Record<string, unknown>;
  currentStep: number;
  totalSteps: number;
  config: TemplateConfig | null;
}

export type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

interface AutoSaveDraftReturn {
  draftId: string | null;
  isSaving: boolean;
  saveStatus: SaveStatus;
  isLoadingDraft: boolean;
  draftLoaded: boolean;
  loadedDraft: OrgContractDocumentItem | null;
  saveDraftNow: () => void;
  markAsCompleted: (documentJson: string) => Promise<void>;
}

function mergeSignatories(
  existing: ContractSignatory[],
  incoming: SignatoryEntry[]
): ContractSignatory[] {
  const now = new Date().toISOString();
  const existingMap = new Map(existing.map((s) => [s.id, s]));

  return incoming.map((entry) => {
    const prev = existingMap.get(entry.id);
    if (prev) {
      return {
        ...prev,
        name: entry.name,
        email: entry.email,
        title: entry.title,
        phone: entry.phone,
        company: entry.company,
        address: entry.address,
      };
    }
    return {
      id: entry.id,
      partyType: entry.partyType,
      name: entry.name,
      email: entry.email,
      title: entry.title,
      phone: entry.phone,
      company: entry.company,
      address: entry.address,
      status: 'pending',
      updatedTime: now,
    };
  });
}

export function useAutoSaveDraft({
  formData,
  enrichmentContext,
  dynamicFieldsCache,
  currentStep,
  totalSteps,
  config,
}: AutoSaveDraftOptions): AutoSaveDraftReturn {
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { userId, user, orgUDSId, sigOrgId, isAuthenticated } = useAuthStore();
  const guestSessionId = useGuestSessionStore((s) => s.guestSessionId);

  // The two ownership modes are mutually exclusive: org users save under
  // OrganizationId; guests save under the sentinel + GuestSessionId.
  const isGuest = !isAuthenticated && !!guestSessionId;

  const draftIdRef = useRef<string | null>(searchParams.get('document'));
  const [draftId, setDraftId] = useState<string | null>(draftIdRef.current);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>('idle');
  const [isLoadingDraft, setIsLoadingDraft] = useState(!!draftIdRef.current);
  const savedTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [loadedDraft, setLoadedDraft] = useState<OrgContractDocumentItem | null>(null);

  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const signatoriesRef = useRef<ContractSignatory[]>([]);
  const hasInitialSave = useRef(false);
  const lastSavedSnapshotRef = useRef<string>('');
  const saveInFlightRef = useRef(false);
  const pendingSaveRef = useRef(false);

  const userName = user ? `${user.firstName} ${user.lastName}`.trim() : '';
  const userPhoto = user?.profileImageUrl ?? '';
  const safeUserId = userId ?? '';

  // -----------------------------------------------------------------------
  // Load existing draft on mount
  // -----------------------------------------------------------------------
  useEffect(() => {
    const existingDraftId = searchParams.get('document');
    if (!existingDraftId) {
      setIsLoadingDraft(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await getContractDocuments({
          where: { ItemId: { eq: existingDraftId } },
          paging: { pageNo: 1, pageSize: 1 },
        });

        if (cancelled) return;

        const item = result.items[0] as OrgContractDocumentItem | undefined;
        if (item) {
          draftIdRef.current = item.ItemId;
          setDraftId(item.ItemId);
          signatoriesRef.current = parseSignatories(item.Signatories);
          hasInitialSave.current = true;
          setLoadedDraft(item);
          setDraftLoaded(true);
        }
      } catch (err) {
        console.warn('Failed to load draft:', err);
      } finally {
        if (!cancelled) setIsLoadingDraft(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------------
  // Build snapshot string for change detection
  // -----------------------------------------------------------------------
  const buildSnapshot = useCallback(() => {
    return JSON.stringify({ formData, enrichmentContext, dynamicFieldsCache, currentStep });
  }, [formData, enrichmentContext, dynamicFieldsCache, currentStep]);

  // -----------------------------------------------------------------------
  // Core save
  // -----------------------------------------------------------------------
  const doSave = useCallback(async () => {
    if (!config) return;
    // Either org context or guest session must be in place; otherwise we
    // have no ownership marker to write.
    if (!orgUDSId && !isGuest) return;

    // Mutex: if a save is already in flight, mark a pending re-save and return
    if (saveInFlightRef.current) {
      pendingSaveRef.current = true;
      return;
    }

    const snapshot = buildSnapshot();
    if (snapshot === lastSavedSnapshotRef.current) return;

    saveInFlightRef.current = true;
    setIsSaving(true);
    setSaveStatus('saving');
    if (savedTimerRef.current) clearTimeout(savedTimerRef.current);

    try {
      const progressPercent =
        totalSteps > 0 ? Math.round(((currentStep + 1) / totalSteps) * 100) : 0;

      const signatories = formData.signatories
        ? mergeSignatories(signatoriesRef.current, formData.signatories as SignatoryEntry[])
        : signatoriesRef.current;
      signatoriesRef.current = signatories;

      if (!draftIdRef.current) {
        const ownershipFields: Pick<
          OrgContractDocumentInsertInput,
          'OrganizationId' | 'OrganizationIds' | 'GuestSessionId'
        > = isGuest
          ? {
              OrganizationId: GUEST_ORG_SENTINEL,
              GuestSessionId: guestSessionId ?? null,
            }
          : {
              OrganizationId: orgUDSId as string,
              OrganizationIds: [orgUDSId as string],
            };

        const input: OrgContractDocumentInsertInput = {
          ...ownershipFields,
          // Stamp the owning org's signature OrganizationId for logged-in users.
          ...(!isGuest && sigOrgId ? { SigOrgId: sigOrgId } : {}),
          Name: config.title,
          TemplateId: config.id,
          TemplateSlug: config.slug,
          TemplateTitle: config.title,
          Status: 'draft',
          IsArchived: false,
          FormData: JSON.stringify(formData),
          EnrichmentContext: JSON.stringify(enrichmentContext),
          DynamicFields: JSON.stringify(dynamicFieldsCache),
          CurrentStep: currentStep,
          ProgressPercent: progressPercent,
          Signatories: serializeSignatories(signatories),
        };

        const res = await createContractDocument(input);
        const newId = res.insertOrgContractDocument.itemId;
        if (!newId) throw new Error('Insert succeeded but no itemId returned');

        draftIdRef.current = newId;
        setDraftId(newId);
        hasInitialSave.current = true;

        // Best-effort: append a 'draft' event to OrgContractDocumentEvent.
        // Failure here must not block draft creation.
        try {
          await createContractDocumentEvent({
            ContractDocumentId: newId,
            OrganizationId: isGuest ? GUEST_ORG_SENTINEL : (orgUDSId as string),
            EventType: 'draft',
            OccurredAt: new Date().toISOString(),
            ActorUserId: safeUserId,
            ActorUserName: userName,
            ActorUserPhoto: userPhoto,
          });
        } catch (eventErr) {
          console.warn('Failed to record draft event:', eventErr);
        }

        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.set('document', newId);
            return next;
          },
          { replace: true }
        );
      } else {
        const input: OrgContractDocumentUpdateInput = {
          FormData: JSON.stringify(formData),
          EnrichmentContext: JSON.stringify(enrichmentContext),
          DynamicFields: JSON.stringify(dynamicFieldsCache),
          CurrentStep: currentStep,
          ProgressPercent: progressPercent,
          Signatories: serializeSignatories(signatories),
        };

        await updateContractDocument(draftIdRef.current, input);
      }

      lastSavedSnapshotRef.current = snapshot;
      setSaveStatus('saved');
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 3_000);
    } catch (err) {
      console.warn('Auto-save failed:', err);
      setSaveStatus('error');
      savedTimerRef.current = setTimeout(() => setSaveStatus('idle'), 5_000);
    } finally {
      saveInFlightRef.current = false;
      setIsSaving(false);

      if (pendingSaveRef.current) {
        pendingSaveRef.current = false;
        doSave();
      }
    }
  }, [
    config,
    orgUDSId,
    sigOrgId,
    isGuest,
    guestSessionId,
    buildSnapshot,
    totalSteps,
    currentStep,
    formData,
    enrichmentContext,
    dynamicFieldsCache,
    safeUserId,
    userName,
    userPhoto,
    setSearchParams,
  ]);

  // -----------------------------------------------------------------------
  // Debounced save on formData / enrichment / dynamicFields changes
  // -----------------------------------------------------------------------
  useEffect(() => {
    if (!config || isLoadingDraft) return;

    const formHasData = Object.keys(formData).length > 0;
    if (!formHasData) return;

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      doSave();
    }, DEBOUNCE_MS);

    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, [formData, enrichmentContext, dynamicFieldsCache, config, isLoadingDraft, doSave]);

  // -----------------------------------------------------------------------
  // Immediate save on step change
  // -----------------------------------------------------------------------
  const prevStepRef = useRef(currentStep);
  useEffect(() => {
    if (prevStepRef.current !== currentStep && !isLoadingDraft && config) {
      prevStepRef.current = currentStep;
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      doSave();
    }
  }, [currentStep, isLoadingDraft, config, doSave]);

  // -----------------------------------------------------------------------
  // Manual save trigger
  // -----------------------------------------------------------------------
  const saveDraftNow = useCallback(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    doSave();
  }, [doSave]);

  // -----------------------------------------------------------------------
  // Mark as completed (called from useGenerationFlow)
  // -----------------------------------------------------------------------
  const markAsCompleted = useCallback(
    async (documentJson: string) => {
      if (!draftIdRef.current) return;
      const completedAt = new Date().toISOString();

      try {
        const result = await updateContractDocument(draftIdRef.current, {
          Status: 'generation_completed',
          Document: documentJson,
          CompletedAt: completedAt,
          ProgressPercent: 100,
        });

        const mutation = result.updateOrgContractDocument;
        if (!mutation?.acknowledged || mutation.totalImpactedData < 1) {
          throw new Error('The completed contract was not persisted');
        }
      } catch (err) {
        console.warn('Failed to mark draft as completed:', err);
        return;
      }

      if (isAuthenticated) {
        try {
          await updateSubscriptionUsage({
            featureId: LEGAL_TEMPLATE_DOCUMENT_GENERATION_FEATURE_ID,
            usage: 1,
          });
          void queryClient.invalidateQueries({
            queryKey: activeSubscriptionQueryKey(userId, sigOrgId),
          });
        } catch (usageError) {
          console.warn(
            'Contract was generated, but subscription usage could not be updated:',
            usageError
          );
        }
      }

      // Best-effort: append a 'generation_completed' event. Failure shouldn't
      // roll back the document update.
      try {
        await createContractDocumentEvent({
          ContractDocumentId: draftIdRef.current,
          OrganizationId: isGuest ? GUEST_ORG_SENTINEL : (orgUDSId as string),
          EventType: 'generation_completed',
          OccurredAt: completedAt,
          ActorUserId: safeUserId,
          ActorUserName: userName,
          ActorUserPhoto: userPhoto,
        });
      } catch (eventErr) {
        console.warn('Failed to record generation_completed event:', eventErr);
      }
    },
    [
      safeUserId,
      userName,
      userPhoto,
      isGuest,
      orgUDSId,
      isAuthenticated,
      queryClient,
      userId,
      sigOrgId,
    ]
  );

  return {
    draftId,
    isSaving,
    saveStatus,
    isLoadingDraft,
    draftLoaded,
    loadedDraft,
    saveDraftNow,
    markAsCompleted,
  };
}
